import * as angular from 'angular';
import * as _ from 'lodash';

import { IRootScope } from '../../IRootScope';
import { toggleEditMode, refreshClick } from '../../common/events';
import {
    Shape, ContextualField, Miniature, IValueViewModel, ComponentDefinition, Group, RefreshClickData, Dictionary
} from '../../models';
import {
    ILoggerService,
    IDateRangeService,
    IStoreService,
    IValidationPageConfigurationService
} from '../../services';
import { IWidget } from '../../widgets';
import { editGroupWidgetState } from '../../common';
import { Store, StoreElement } from '../../stateManagement';
import { shapeControllerCommon as common } from './editShapes.common';

class EditShapesGroupController implements angular.IComponentController {
    canBind: boolean;
    fields: ContextualField[];
    formController: angular.IFormController;
    groups: Dictionary<Group>;
    isLoading: boolean;
    isEditMode: boolean;
    item: IWidget;
    loadingMessage: string;
    miniatures: Miniature[];
    pointsFormController: angular.IFormController;
    selectedShape: Shape;
    viewModels: Dictionary<IValueViewModel>;
    viewModelList: IValueViewModel[];

    private _storeLoaded: angular.IDeferred<void>;
    private _widgets: IWidget[];
    private _unsubscribe: () => void;

    static $inject = [
        '$q',
        '$rootScope',
        '$state',
        '$stateParams',
        'storeService',
        'validationPageConfigurationService',
        'loggerService',
        'dateRangeService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$stateParams: angular.ui.IStateParamsService,
        private _storeService: IStoreService,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
        private _loggerService: ILoggerService,
        private _dateRangeService: IDateRangeService
    ) {
        this._storeLoaded = this._$q.defer<void>();
    }

    $onInit() {
        this.isEditMode = true;
        if (!this._$rootScope.isEditMode) {
            this._$rootScope.$broadcast(toggleEditMode);
        }
        this._dateRangeService.beginDate(this._$stateParams.beginDate);
        this._dateRangeService.endDate(this._$stateParams.endDate);
        this._dateRangeService.initialized(true);
        this.miniatures = common.miniatures;
        this.fields = common.getFields(
            () => this.selectedShape,
            () => this.item.configuration.shapes
        );

        // Connect to store
        this.isLoading = true;
        this._unsubscribe = this._storeService.register(
            store => this.refreshStore(store),
            [ StoreElement.ValueViewModels, StoreElement.Groups ]
        );

        // old fashionned loading
        this._storeLoaded.promise.then(() => this.loadData());
    }

    $onDestroy() {
        try {
            if (this._unsubscribe) {
                this._unsubscribe();
            }
        } catch (e) {
            console.error(e);
        }
    }

    private refreshStore(store: Store) {
        try {
            this.viewModels = store.valueViewModels.dictionary;
            this.viewModelList = _.values(store.valueViewModels.dictionary);
            this.groups = store.groups.dictionary;
            this._storeLoaded.resolve();
            this.onLoad();
        } catch (e) {
            this._loggerService.logError('Cannot load dashboard');
        }
    }

    private onLoad() {
        if (this.item && this.viewModels && this.groups) {
            if (this.item.configuration.shapes && this.item.configuration.shapes.length) {
                this.selectedShape = this.item.configuration.shapes[0];
            }
            this.isLoading = false;
            this._$rootScope.$broadcast(
                refreshClick,
                { beginDate: this._$stateParams.beginDate, endDate: this._$stateParams.endDate } as RefreshClickData
            );
        }
    }

    private loadData() {
        this.isLoading = true;
        this._validationPageConfigurationService
            .get(this._$stateParams.groupGuid, this.viewModels, this.groups)
            .then(widgets => {
                this._widgets = widgets;
                const item = _.find(widgets, w => w.guid === this._$stateParams.widgetGuid);
                if (!item.configuration.shapes) {
                    item.configuration.shapes = [];
                }
                this.item = item;
                this.onLoad();
            }).catch(reason => {
                this._loggerService.logError('Validation page configuration failed to load :' + reason.message ? reason.message : reason);
            }).finally(() => {
                this.isLoading = false;
            });
    }

    onDrop() {
        common.onDrop(this.item, (newshape) => this.selectedShape = newshape);
    }

    addPoint() {
        common.addPoint(this.selectedShape, this.canBind);
    }

    onSave() {
        this.loadingMessage = 'Saving';
        this.isLoading = true;
        const groupGuid = this._$stateParams.groupGuid;
        this._validationPageConfigurationService
            .update(groupGuid, this._widgets)
            .then(() => {
                this.formController.$setPristine();
                this._loggerService.logSuccess(`Widget ${this.item.name} saved successfully.`);
                this.leave(groupGuid);
            }).catch(() => {
                this._loggerService.logError(`Error while saving ${this.item.name} .`);
            }).finally(() => {

            });
    }

    onCancel() {
        this.pointsFormController.$setPristine();
        this.leave(this._$stateParams.groupGuid);
    }

    unselect($event: angular.IAngularEvent) {
        if (this.canSelect() && !$event.defaultPrevented) {
            this.selectedShape = null;
        }
    }

    onDelete(shapes: Shape[]) {
        this.onSave();
    }

    onDuplicate(shape: Shape) {
        this.formController.$getControls().forEach(c => c.$validate());
    }

    gotoSeries() {
        if (!this.canSelect()) {
            this._loggerService.logWarning(`Save or cancel changes before editing series.`);
        } else {
            this._$state.go(editGroupWidgetState, this._$stateParams);
        }
    }

    canSelect() {
        return !(this.pointsFormController?.$dirty || this.formController?.$dirty)
            && !(this.pointsFormController?.$invalid || this.formController?.$invalid);
    }

    private leave(groupGuid) {
        this._$state.go('group.validation', { groupGuid: groupGuid });
    }
}

export const wtEditShapesGroup: ComponentDefinition = {
    componentId: 'wtEditShapesGroup',
    controller: EditShapesGroupController,
    template: require('./editShapes.component.html'),
    controllerAs: 'vm',
    bindings: {}
};